import { Calculator } from 'lucide-react';
import ReportSection from './ReportSection';
import StatusChip from '../StatusChip';
import { tx } from '../../i18n';
import { categoryTitle, isUnrated, orderedCategories } from '../../lib/report';
import { statusOfCategory } from '../../lib/status';
import { num, text } from '../../lib/values';
import type { AnalysisDetailResponse, CategoryDetail } from '../../types/api';

/**
 * How the number on the front was arrived at, in enough detail to arrive at it again.
 *
 * <p>A score a reader cannot take apart is a score they are asked to trust, and this product's
 * whole claim is that it does not ask for trust. So the table shows each category's score, the
 * weight it was given, and the contribution those two multiply to - and the contributions add up
 * to the overall, in front of the reader. Where a source could not be read, its weight is shown
 * as redistributed rather than quietly counted as a zero, because the difference between "scored
 * badly" and "could not be scored" is the difference between a fact and a gap.
 */
export default function ScoreMethod({ detail }: { detail: AnalysisDetailResponse }) {
  const ordered = orderedCategories(detail.categories);
  if (ordered.length === 0) {
    return null;
  }

  const overall = num(detail.overall.score) ?? 0;
  const unratedNames = ordered.filter(isUnrated).map((category) => categoryTitle(category.category));
  const version = text(detail.scoringVersion);

  const contribution = (category: CategoryDetail): number | null => {
    const score = num(category.score);
    const weight = num(category.weightPercent);
    if (score === null || weight === null || isUnrated(category)) {
      return null;
    }
    return Math.round((score * weight) / 100);
  };

  const labels = {
    category: tx('Category', 'Kategori', 'Kategoria', 'Categoria', 'Categoría'),
    score: tx('Score', 'Puan', 'Ocena', 'Pontuação', 'Puntuación'),
    weight: tx('Weight', 'Ağırlık', 'Waga', 'Peso', 'Peso'),
    adds: tx('Adds to the overall', 'Genele katkısı', 'Wkład w ocenę ogólną', 'Contributo para a global', 'Aporta al global'),
  };

  return (
    <ReportSection
      id="section-method"
      icon={Calculator}
      title={tx(
        'How this score is calculated',
        'Bu puan nasıl hesaplanıyor',
        'Jak obliczana jest ta ocena',
        'Como é calculada esta pontuação',
        'Cómo se calcula esta puntuación',
      )}
      note={
        version
          ? tx(
              `Every weight, and the model that applied them (${version}).`,
              `Tüm ağırlıklar ve bunları uygulayan model (${version}).`,
              `Wszystkie wagi i model, który je zastosował (${version}).`,
              `Todos os pesos e o modelo que os aplicou (${version}).`,
              `Todos los pesos y el modelo que los aplicó (${version}).`,
            )
          : tx(
              'Every weight behind the overall number.',
              'Genel puanın arkasındaki tüm ağırlıklar.',
              'Wszystkie wagi stojące za oceną ogólną.',
              'Todos os pesos por trás da pontuação global.',
              'Todos los pesos que hay detrás de la puntuación global.',
            )
      }
    >
      <p className="small">
        {tx(
          'Each category is scored 0 to 100 from its own public dataset, multiplied by its weight, and the results added together. The same formula is used for every home, so two reports can be compared directly.',
          'Her kategori kendi kamu veri kaynağından 0-100 arasında puanlanır, ağırlığıyla çarpılır ve sonuçlar toplanır. Her konut için aynı formül kullanılır; bu yüzden iki rapor doğrudan karşılaştırılabilir.',
          'Każda kategoria jest oceniana od 0 do 100 na podstawie własnego zbioru danych publicznych, mnożona przez swoją wagę, a wyniki są sumowane. Dla każdego mieszkania stosowany jest ten sam wzór, więc dwa raporty można porównywać wprost.',
          'Cada categoria é pontuada de 0 a 100 a partir do seu próprio conjunto de dados públicos, multiplicada pelo seu peso, e os resultados são somados. A mesma fórmula é usada para todas as casas, pelo que dois relatórios podem ser comparados diretamente.',
          'Cada categoría se puntúa de 0 a 100 con su propio conjunto de datos públicos, se multiplica por su peso y los resultados se suman. Se usa la misma fórmula para toda vivienda, así que dos informes se pueden comparar directamente.',
        )}
      </p>

      <table className="data-table data-table--cards">
        <caption className="sr-only">
          {tx(
            'Category scores, weights and their contribution to the overall',
            'Kategori puanları, ağırlıkları ve genele katkıları',
            'Oceny kategorii, ich wagi i wkład w ocenę ogólną',
            'Pontuações das categorias, pesos e contributo para a global',
            'Puntuaciones de las categorías, pesos y su aporte al global',
          )}
        </caption>
        <thead>
          <tr>
            <th scope="col">{labels.category}</th>
            <th scope="col">{labels.score}</th>
            <th scope="col">{labels.weight}</th>
            <th scope="col">{labels.adds}</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((category) => {
            const unrated = isUnrated(category);
            const adds = contribution(category);
            const weight = num(category.weightPercent);
            return (
              <tr key={category.category}>
                <th scope="row" data-label={labels.category}>
                  {categoryTitle(category.category)}
                </th>
                <td data-label={labels.score}>
                  <StatusChip
                    status={statusOfCategory(category)}
                    label={
                      unrated || num(category.score) === null
                        ? tx('No data', 'Veri yok', 'Brak danych', 'Sem dados', 'Sin datos')
                        : `${category.score}/100`
                    }
                  />
                </td>
                <td data-label={labels.weight}>
                  {unrated || weight === null
                    ? tx('Redistributed', 'Dağıtıldı', 'Rozdzielona', 'Redistribuído', 'Redistribuido')
                    : `${weight}%`}
                </td>
                <td data-label={labels.adds}>
                  {adds === null
                    ? tx('Nothing', 'Yok', 'Nic', 'Nada', 'Nada')
                    : tx(
                        `${adds} points`,
                        `${adds} puan`,
                        `${adds} pkt`,
                        `${adds} pontos`,
                        `${adds} puntos`,
                      )}
                </td>
              </tr>
            );
          })}
          <tr className="data-table__total">
            <th scope="row" data-label={labels.category}>
              {tx('Overall', 'Genel', 'Ogółem', 'Global', 'Global')}
            </th>
            <td data-label={labels.score}>
              <strong>{overall}/100</strong>
            </td>
            <td data-label={labels.weight}>100%</td>
            <td data-label={labels.adds}>
              <strong>
                {tx(
                  `${overall} points`,
                  `${overall} puan`,
                  `${overall} pkt`,
                  `${overall} pontos`,
                  `${overall} puntos`,
                )}
              </strong>
            </td>
          </tr>
        </tbody>
      </table>

      {unratedNames.length > 0 ? (
        <p className="small">
          <strong>
            {tx(
              'What the missing data did:',
              'Eksik verinin etkisi:',
              'Co zrobiły brakujące dane:',
              'O que os dados em falta fizeram:',
              'Qué han hecho los datos que faltan:',
            )}
          </strong>{' '}
          {tx(
            `${unratedNames.join(', ')} could not be read for this address, so it carries no weight here and the other categories were rescaled to 100%. It was not counted as a zero, and it was not counted as good news either — it simply is not in this number.`,
            `${unratedNames.join(', ')} bu adres için okunamadı; bu yüzden burada ağırlığı yok ve diğer kategoriler %100'e yeniden ölçeklendi. Sıfır sayılmadı, iyi haber olarak da sayılmadı — bu rakamın içinde yok.`,
            `${unratedNames.join(', ')} nie dało się odczytać dla tego adresu, więc nie ma tu żadnej wagi, a pozostałe kategorie przeskalowano do 100%. Nie policzono tego jako zera ani jako dobrej wiadomości — po prostu nie ma tego w tej liczbie.`,
            `${unratedNames.join(', ')} não pôde ser lido para esta morada, pelo que não tem peso aqui e as restantes categorias foram reescaladas para 100%. Não foi contado como zero, nem como boa notícia — simplesmente não está neste número.`,
            `${unratedNames.join(', ')} no se ha podido leer para esta dirección, así que aquí no pesa nada y las demás categorías se han reescalado al 100%. No se ha contado como un cero, ni como una buena noticia: sencillamente no está en esta cifra.`,
          )}
        </p>
      ) : null}

      <p className="small muted">
        {tx(
          'A category is scored against other homes in the same English region, so a London flat is not judged by a national average. Contributions are rounded for display, so they may add up to a point either side of the overall.',
          'Her kategori aynı İngiltere bölgesindeki diğer konutlara göre puanlanır; yani bir Londra dairesi ülke ortalamasıyla yargılanmaz. Katkılar gösterim için yuvarlanır; bu yüzden toplamları genel puandan bir puan sapabilir.',
          'Kategoria jest oceniana na tle innych mieszkań w tym samym regionie Anglii, więc mieszkanie w Londynie nie jest oceniane średnią krajową. Wkłady są zaokrąglane na potrzeby wyświetlania, więc ich suma może różnić się o punkt od oceny ogólnej.',
          'Cada categoria é pontuada face a outras casas da mesma região de Inglaterra, pelo que um apartamento em Londres não é julgado por uma média nacional. Os contributos são arredondados para apresentação, pelo que a soma pode variar um ponto face à global.',
          'Cada categoría se puntúa frente a otras viviendas de la misma región de Inglaterra, así que un piso de Londres no se juzga con una media nacional. Los aportes se redondean al mostrarlos, así que su suma puede variar un punto respecto al global.',
        )}
        {version
          ? ' '
            + tx(
                `Scoring model ${version}.`,
                `Puanlama modeli ${version}.`,
                `Model oceny ${version}.`,
                `Modelo de pontuação ${version}.`,
                `Modelo de puntuación ${version}.`,
              )
          : ''}
      </p>
    </ReportSection>
  );
}
